import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Layers, Square, CheckCircle2, Phone, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { getMaterials } from '../data/api';
import { useWhatsApp } from '../hooks/useWhatsApp';

const iconMap = {
  'Layers': Layers,
  'Square': Square,
};

const BuildingMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const { openChat } = useWhatsApp();

  useEffect(() => {
    getMaterials().then(data => {
      setMaterials(data);
      if (data.length > 0) setSelectedMaterial(data[0]);
    }).catch(() => {});
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!selectedMaterial) return null;

  const IconComponent = iconMap[selectedMaterial.icon] || Layers;

  const nextImage = () => {
    if (selectedMaterial.galleryImages && selectedMaterial.galleryImages.length > 0) {
      setActiveImageIndex((prev) => 
        prev === selectedMaterial.galleryImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedMaterial.galleryImages && selectedMaterial.galleryImages.length > 0) {
      setActiveImageIndex((prev) => 
        prev === 0 ? selectedMaterial.galleryImages.length - 1 : prev - 1
      );
    }
  };

  return (
    <section id="building-materials" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - Compact on mobile */}
        <div className="text-center mb-6 sm:mb-8 lg:mb-12">
          <Badge className="mb-2 sm:mb-4 bg-blue-100 text-blue-700 px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm hover:bg-blue-100">
            Supply & Installation
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
            Building Materials
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto hidden sm:block">
            Premium quality materials for every construction need with professional installation services.
          </p>
        </div>

        {/* Horizontal Scrollable Category Tabs */}
        <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {materials.map((material) => {
            const ItemIcon = iconMap[material.icon] || Layers;
            const isSelected = selectedMaterial.id === material.id;
            
            return (
              <button
                key={material.id}
                onClick={() => {
                  setSelectedMaterial(material);
                  setActiveImageIndex(0);
                }}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-full whitespace-nowrap transition-all duration-200 ${
                  isSelected 
                    ? 'bg-blue-600 text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ItemIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base font-medium">{material.name}</span>
              </button>
            );
          })}
        </div>

        {/* Content Card - Compact for mobile */}
        <div className="bg-gray-50 rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
          {/* Material Header */}
          <div className="bg-white p-4 sm:p-6 border-b border-gray-100">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                <IconComponent className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <Badge className="mb-1 bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs">
                  {selectedMaterial.category}
                </Badge>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{selectedMaterial.name}</h3>
              </div>
            </div>
          </div>

          {/* Image Gallery - Compact with navigation arrows */}
          {selectedMaterial.galleryImages && selectedMaterial.galleryImages.length > 0 && (
            <div className="relative bg-white">
              {/* Main Image */}
              <div className="relative aspect-[16/10] sm:aspect-[16/9] bg-gray-100">
                <img 
                  src={selectedMaterial.galleryImages[activeImageIndex]} 
                  alt={selectedMaterial.name}
                  className="w-full h-full object-contain"
                />
                
                {/* Navigation Arrows */}
                {selectedMaterial.galleryImages.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                    >
                      <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                    >
                      <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                  {activeImageIndex + 1} / {selectedMaterial.galleryImages.length}
                </div>
              </div>
              
              {/* Thumbnail Gallery - Hidden on small mobile, shown on larger screens */}
              <div className="hidden sm:flex gap-2 p-3 overflow-x-auto bg-gray-50">
                {selectedMaterial.galleryImages.map((img, idx) => (
                  <button
                    key={`mat-${img.slice(-20)}`}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-lg overflow-hidden border-2 transition-all ${
                      activeImageIndex === idx ? 'border-blue-600' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain bg-white" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Content Section - Compact */}
          <div className="p-4 sm:p-6">
            {/* Description */}
            <p className="text-sm sm:text-base text-gray-600 mb-4 leading-relaxed line-clamp-3 sm:line-clamp-none">
              {selectedMaterial.description}
            </p>

            {/* Features - Horizontal scroll on mobile */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Features</h4>
              <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:overflow-visible">
                {selectedMaterial.features.slice(0, 4).map((feature) => (
                  <div key={feature} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-full border border-gray-200 whitespace-nowrap flex-shrink-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Applications - Compact */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Applications</h4>
              <div className="flex flex-wrap gap-1.5">
                {selectedMaterial.applications.map((app) => (
                  <Badge key={app} variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 text-xs px-2 py-0.5">
                    {app}
                  </Badge>
                ))}
              </div>
            </div>

            {/* CTA Buttons - Full width on mobile */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 border-t border-gray-100">
              <Button
                onClick={scrollToContact}
                className="bg-blue-600 hover:bg-blue-700 text-white flex-1 py-2.5 sm:py-3 text-sm"
              >
                <Phone className="w-4 h-4 mr-2" />
                Request Quote
              </Button>
              <Button
                variant="outline"
                className="border-gray-300 hover:border-blue-600 hover:text-blue-600 flex-1 py-2.5 sm:py-3 text-sm"
                onClick={() => {
                  openChat({ page: 'materials', context: selectedMaterial.name, customMessage: `Hi! I'm interested in ${selectedMaterial.name}. Can you provide more information?` });
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Stats Banner - More compact */}
        <div className="mt-6 sm:mt-8 lg:mt-12 grid grid-cols-4 gap-2 sm:gap-4 lg:gap-6">
          <div className="text-center p-2 sm:p-4 lg:p-6 bg-gray-50 rounded-lg sm:rounded-xl">
            <div className="text-lg sm:text-xl lg:text-3xl font-bold text-blue-600 mb-0.5 sm:mb-1">150+</div>
            <div className="text-[10px] sm:text-xs lg:text-sm text-gray-600">Projects</div>
          </div>
          <div className="text-center p-2 sm:p-4 lg:p-6 bg-gray-50 rounded-lg sm:rounded-xl">
            <div className="text-lg sm:text-xl lg:text-3xl font-bold text-blue-600 mb-0.5 sm:mb-1">38+</div>
            <div className="text-[10px] sm:text-xs lg:text-sm text-gray-600">Years</div>
          </div>
          <div className="text-center p-2 sm:p-4 lg:p-6 bg-gray-50 rounded-lg sm:rounded-xl">
            <div className="text-lg sm:text-xl lg:text-3xl font-bold text-blue-600 mb-0.5 sm:mb-1">100%</div>
            <div className="text-[10px] sm:text-xs lg:text-sm text-gray-600">Quality</div>
          </div>
          <div className="text-center p-2 sm:p-4 lg:p-6 bg-gray-50 rounded-lg sm:rounded-xl">
            <div className="text-lg sm:text-xl lg:text-3xl font-bold text-blue-600 mb-0.5 sm:mb-1">24/7</div>
            <div className="text-[10px] sm:text-xs lg:text-sm text-gray-600">Support</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuildingMaterials;
